/**
 * React root — WP7 client entry point.
 *
 * Strict-mode on. Socket connect must be idempotent to survive React
 * 18's double-mount effect semantics (see the `WsClientProvider`).
 */

import { StrictMode, type ReactElement } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.js";
import { StoreProvider } from "./store/context.js";
import { INITIAL_STATE } from "./store/types.js";
import { loadChatEnabled } from "./prefs.js";
import { WsClientProvider } from "./net/WsClientProvider.js";
import { SoundProvider } from "./sound/SoundProvider.js";
import { KeymapProvider } from "./input/keybinding/index.js";
import { WasdStrafeModeProvider } from "./input/wasdStrafeMode.js";
import { SpellQueueWindowProvider } from "./input/spellQueueWindow.js";

const el = document.getElementById("app");
if (el === null) {
  throw new Error("main: #app container missing from index.html");
}

// StrictMode is unconditional: PitSocket.connect() revives a disposed
// instance, so the mount → cleanup → mount cycle is safe (see socket.ts).
// The e2e harness runs the same tree as production.
const tree: ReactElement = (
  // #855: the persisted chat on/off preference is seeded into the store at
  // construction rather than read from a provider at each use site, because
  // the reducer is where the "chat off ⇒ the chat box cannot be open"
  // invariant is enforced (see `StoreState.chatEnabled`).
  <StoreProvider initial={{ ...INITIAL_STATE, chatEnabled: loadChatEnabled() }}>
    <WsClientProvider>
      <SoundProvider>
        <KeymapProvider>
          <WasdStrafeModeProvider>
            {/* #826: the spell-queue window (#797) is a live, settings-driven
                input-timing preference — same provider shape as the WASD
                strafe mode above, so a change from the settings screen
                reaches the mounted `InputSurface` without a reload. */}
            <SpellQueueWindowProvider>
              <App />
            </SpellQueueWindowProvider>
          </WasdStrafeModeProvider>
        </KeymapProvider>
      </SoundProvider>
    </WsClientProvider>
  </StoreProvider>
);

createRoot(el).render(
  <StrictMode>{tree}</StrictMode>,
);
